import axios from 'axios';
import { getAuthToken, clearAuthToken, isElectron } from '../lib/authToken';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true, // Cookie-based auth (web)
});

// Electron: Bearer token + no credentials (avoids CORS credential issues)
api.interceptors.request.use((config) => {
  if (isElectron()) {
    config.withCredentials = false;
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Electron: clear stored token on 401 (expired/invalid)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (isElectron() && err.response?.status === 401) clearAuthToken();
    return Promise.reject(err);
  }
);

export default api;

// Test device connection (SNMP or SSH) for a specific device by ID
export async function testDeviceConnection(id: string | number) {
  const res = await api.post(`/devices/${id}/test-connection`, {}, { withCredentials: true });
  return res.data;
}


// Add a new device
export async function addDevice(device: any) {
  const res = await api.post('/devices/', device, { withCredentials: true });
  return res.data;
} 

// Get a device by ID
export async function getDevice(id: string | number) {
  const res = await api.get(`/devices/${id}`, { withCredentials: true });
  return res.data;
} 


// Get notifications (backend notifications service)
export async function getNotifications() {
  const res = await api.get('/notifications', { withCredentials: true });
  return res.data;
}

// Mark notification as read
export async function markNotificationRead(id: string | number) {
  const res = await api.patch(`/notifications/${id}/read`, {}, { withCredentials: true });
  return res.data;
}

// Get alerts (supports filters)
export async function getAlerts(params: { deviceId?: string | number; severity?: string; threshold?: number; acknowledged?: boolean; resolved?: boolean } = {}) {
  const res = await api.get('/alerts', { params, withCredentials: true });
  return res.data;
}

// Create a new alert
export async function createAlert(alert: any) {
  const res = await api.post('/alerts', alert, { withCredentials: true });
  return res.data;
}

// Acknowledge an alert
export async function acknowledgeAlert(id: string | number) {
  const res = await api.put(`/alerts/${id}/acknowledge`, {}, { withCredentials: true });
  return res.data;
}

// Resolve an alert
export async function resolveAlert(id: string | number) {
  const res = await api.put(`/alerts/${id}/resolve`, {}, { withCredentials: true });
  return res.data;
}

// Delete an alert
export async function deleteAlert(id: string | number) {
  const res = await api.delete(`/alerts/${id}`, { withCredentials: true });
  return res.data;
}

// Assign an alert to groups and/or users
export async function assignAlert(id: string | number, assignmentData: {
  groupIds?: string[];
  userIds?: string[];
  extraEmails?: string[];
  notify?: boolean;
}) {
  // Build clean data object - only include fields that have values
  const cleanData: any = {};
  
  // Always include notify (defaults to true if not specified)
  // Backend expects boolean, so we always send it
  cleanData.notify = assignmentData.notify !== undefined ? assignmentData.notify : true;
  
  // Only include arrays if they have values (since they're optional in backend)
  // Backend validation requires at least one of groupIds, userIds, or extraEmails
  if (assignmentData.groupIds && Array.isArray(assignmentData.groupIds) && assignmentData.groupIds.length > 0) {
    cleanData.groupIds = assignmentData.groupIds;
  }
  if (assignmentData.userIds && Array.isArray(assignmentData.userIds) && assignmentData.userIds.length > 0) {
    cleanData.userIds = assignmentData.userIds;
  }
  if (assignmentData.extraEmails && Array.isArray(assignmentData.extraEmails) && assignmentData.extraEmails.length > 0) {
    // Validate email format before sending
    const validEmails = assignmentData.extraEmails.filter(email => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    });
    if (validEmails.length > 0) {
      cleanData.extraEmails = validEmails;
    }
  }
  
  // Log the request for debugging
  console.log("Assigning alert:", { id, cleanData });
  console.log("Request URL:", `/alerts/${id}/assign`);
  console.log("Request body:", JSON.stringify(cleanData, null, 2));
  
  try {
    const res = await api.put(`/alerts/${id}/assign`, cleanData, { withCredentials: true });
    return res.data;
  } catch (error: any) {
    // Log detailed error for debugging
    const errorResponse = error.response?.data;
    console.error("Assign alert API error:", {
      status: error.response?.status,
      data: errorResponse,
      message: error.message,
      requestData: cleanData,
      validationErrors: errorResponse?.data
    });
    
    // Log validation errors in detail
    if (errorResponse?.data && Array.isArray(errorResponse.data)) {
      console.error("VALIDATION ERRORS DETAIL:", JSON.stringify(errorResponse.data, null, 2));
      errorResponse.data.forEach((err: any, idx: number) => {
        console.error(`Error ${idx + 1}:`, {
          field: err.field,
          message: err.message,
          type: err.type,
          value: err.value,
          expected: err.expected
        });
      });
    }
    
    throw error;
  }
}

// Get all devices
export async function getDevices() {
  const res = await api.get('/devices', { withCredentials: true });
  return res.data;
} 

// Update a device by ID
export async function updateDevice(id: string | number, device: any) {
  const res = await api.put(`/devices/${id}`, device, { withCredentials: true });
  return res.data;
}

// Delete a device by ID
export async function deleteDevice(id: string | number) {
  const res = await api.delete(`/devices/${id}`, { withCredentials: true });
  return res.data;
}

// Get alerts for a specific device
export async function getDeviceAlerts(id: string | number) {
  const res = await api.get(`/devices/${id}/alerts`, { withCredentials: true });
  return res.data;
}



// Get sheduler devices
export async function getShedulerDevices() {
  const res = await api.get('/devices/sheduler', { withCredentials: true });
  return res.data;
}

// Run network test for a device
export async function runNetworkTest(deviceId: string | number, params: { 
  type: string, 
  port?: number, 
  protocol?: string, 
  command?: string,
  url?: string,
  method?: string
}) {
  const res = await api.post(`/devices/${deviceId}/network-test`, params, { withCredentials: true });
  return res.data;
}

// Get device scheduler
export async function getDeviceScheduler() {
  const res = await api.get('/devices/sheduler', { withCredentials: true });
  return res.data;
}

//Login
export async function login({ username, password }: { username: string; password: string }) {
  const res = await api.post('/users/auth/login', { username, password });
  return res.data;
}

// Signup
export async function signUp({ username, password, displayname, email, organizationType, organizationName, joinCode, active }: { username: string; password: string; displayname: string; email: string; organizationType: string; organizationName?: string; joinCode?: string; active: boolean }) {
  const res = await api.post('/users', {
    username,
    password,
    displayname,
    email,
    organizationType,
    organizationName,
    joinCode,
    active,
  }, { withCredentials: true });
  return res.data;
}

// Logout
export async function logout() {
  const res = await api.post('/users/auth/logout', {}, { withCredentials: true });
  return res.data;
}

// Get current user profile (me)
export async function getMe() {
  const res = await api.get('/me', { withCredentials: true });
  return res.data;
}

// Reset password
export async function resetPassword(token: string, newPassword: string, confirmPassword: string) {
  const res = await api.post(`/users/reset-password/${token}`, {
    newPassword,
    confirmPassword,
  });
  return res.data;
}

// Forgot password
export async function forgotPassword(email: string) {
  const res = await api.post('/users/forgot-password', { email });
  return res.data;
}

// Get organization details by ID
export async function getOrganizationById(id: string) {
  const res = await api.get(`/users/organization/${id}`, { withCredentials: true });
  return res.data.Organization;
}

// Influx-backed metrics: Device dashboard (system, status, interfaces)
export async function getInfluxDeviceDashboard(
  deviceId: string | number,
  params: { start?: string; window?: string } = {}
) {
  const res = await api.get(`/devices_metrics/dashboard/${deviceId}`,
    { params, withCredentials: true }
  );
  return res.data;
}

// Influx-backed metrics: System metrics
export async function getInfluxSystemMetrics(
  deviceId: string | number,
  params: { start?: string; end?: string; window?: string } = {}
) {
  const res = await api.get(`/devices_metrics/system/${deviceId}`,
    { params, withCredentials: true }
  );
  return res.data;
}

// Influx-backed metrics: Device status
export async function getInfluxDeviceStatus(
  deviceId: string | number,
  params: { start?: string; end?: string; window?: string } = {}
) {
  const res = await api.get(`/devices_metrics/status/${deviceId}`,
    { params, withCredentials: true }
  );
  return res.data;
}

// Influx-backed metrics: Interface metrics
export async function getInfluxInterfaceMetrics(
  deviceId: string | number,
  params: { interfaceName?: string; start?: string; end?: string; window?: string } = {}
) {
  const res = await api.get(`/devices_metrics/interfaces/${deviceId}`,
    { params, withCredentials: true }
  );
  return res.data;
}

// Influx-backed metrics: Disk metrics
export async function getInfluxDiskMetrics(
  deviceId: string | number,
  params: { start?: string; window?: string } = {}
) {
  const res = await api.get(`/devices_metrics/disk/${deviceId}`,
    { params, withCredentials: true }
  );
  return res.data;
}

// Influx-backed metrics: Top interfaces
export async function getInfluxTopInterfaces(
  deviceId: string | number,
  params: { start?: string; limit?: number } = {}
) {
  const res = await api.get(`/devices_metrics/topinterfaces/${deviceId}`,
    { params, withCredentials: true }
  );
  return res.data;
}

// Influx-backed metrics: Multi-device overview
export async function getInfluxMultiDeviceOverview({ start }: { start: string }) {
  const res = await api.get(`/devices_metrics/overview?start=${encodeURIComponent(start)}`, { withCredentials: true });
  return res.data;
}

// User Management APIs

// Get all users
export async function getUsers() {
  const res = await api.get('/users', { withCredentials: true });
  return res.data;
}

// Get user by ID
export async function getUser(id: string | number) {
  const res = await api.get(`/users/${id}`, { withCredentials: true });
  return res.data;
}

// Create a new user
export async function createUser(userData: {
  username: string;
  password: string;
  displayname: string;
  email: string;
  role?: string;
  active: boolean;
  organizationType: 'create' | 'join';
  organizationName?: string;
  joinCode?: string;
}) {
  const res = await api.post('/users', userData, { withCredentials: true });
  return res.data;
}

// Update user
export async function updateUser(id: string | number, userData: {
  username?: string;
  password?: string;
  displayname?: string;
  email?: string;
  role?: string;
  active?: boolean;
}) {
  const res = await api.put(`/users/${id}`, userData, { withCredentials: true });
  return res.data;
}

// Delete user
export async function deleteUser(id: string | number) {
  const res = await api.delete(`/users/${id}`, { withCredentials: true });
  return res.data;
}

// Update user role
export async function updateUserRole(id: string | number, role: string) {
  const res = await api.put(`/users/${id}`, { role }, { withCredentials: true });
  return res.data;
}

// Audit Logs APIs

// Get audit logs with optional filtering
export async function getAuditLogs(params: { days?: number; limit?: number; skip?: number } = {}) {
  const days = Number(params.days || 7);
  const limit = Number(params.limit || 500); // Default to 500 to get more data
  const skip = Number(params.skip || 0);
  
  try {
    // Use the simplest approach that works with Moleculer's validation
    const res = await api.get('/audits/logs', {
      params: { days, limit, skip },
      withCredentials: true,
      validateStatus: (status) => {
        // Accept both success and 422 status codes
        return (status >= 200 && status < 300) || status === 422;
      }
    });
    
    // If we got a 422, return empty data with new format
    if (res.status === 422) {
      return { logs: [], days, limit, skip, total: 0, count: 0, hasMore: false };
    }
    
    return res.data;
  } catch (error: any) {
    // Handle any other errors gracefully
    console.error('Audit logs error:', error);
    return { logs: [], days, limit, skip, total: 0, count: 0, hasMore: false };
  }
}

// Get audit logs statistics
export async function getAuditStats(params: { days?: number } = {}) {
  const days = Number(params.days || 7);
  
  try {
    // Use the simplest approach that works with Moleculer's validation
    const res = await api.get('/audits/stats', {
      params: { days },
      withCredentials: true,
      validateStatus: (status) => {
        // Accept both success and 422 status codes
        return (status >= 200 && status < 300) || status === 422;
      }
    });
    
    // If we got a 422, return empty data
    if (res.status === 422) {
      return {
        period: `${days} days`,
        actions: [],
        errors: [],
        users: [],
        generated: new Date().toISOString()
      };
    }
    
    return res.data;
  } catch (error: any) {
    // Handle any other errors gracefully
    console.error('Audit stats error:', error);
    return {
      period: `${days} days`,
      actions: [],
      errors: [],
      users: [],
      generated: new Date().toISOString()
    };
  }
}

// ML Service APIs

// Predict CPU usage for a device
export async function predictCpuUsage(deviceId: string | number, hoursAhead: number = 24) {
  const res = await api.post('/ml/predictCpuUsage', {
    deviceId,
    hoursAhead
  }, { withCredentials: true });
  return res.data;
}

// Predict memory usage for a device
export async function predictMemoryUsage(deviceId: string | number, hoursAhead: number = 24) {
  const res = await api.post('/ml/predictMemoryUsage', {
    deviceId,
    hoursAhead
  }, { withCredentials: true });
  return res.data;
}

// Detect anomalies for a device
export async function detectAnomalies(deviceId: string | number, sensitivity: number = 0.1) {
  const res = await api.post('/ml/detectAnomalies', {
    deviceId,
    sensitivity
  }, { withCredentials: true });
  return res.data;
}

// Get ML service health
export async function getMLHealth() {
  const res = await api.get('/ml/health', { withCredentials: true });
  return res.data;
}

// Get available ML algorithms
export async function getMLAlgorithms() {
  const res = await api.get('/ml/algorithms', { withCredentials: true });
  return res.data;
}

// Get all devices for ML analysis
export async function getDevicesForML() {
  const res = await api.get('/devices', { withCredentials: true });
  return res.data;
}

// Get device metrics for ML analysis
export async function getDeviceMetrics(deviceId: string | number, start: string = "-24h", limit: number = 100) {
  const res = await api.get(`/devices/${deviceId}/metrics`, {
    params: { start, limit },
    withCredentials: true
  });
  return res.data;
}

// Get ML insights summary
export async function getMLInsights(mode: string = 'local') {
  const res = await api.get('/ml/insights', { 
    params: { mode },
    withCredentials: true 
  });
  return res.data;
}

// Get anomaly detection summary
export async function getAnomalySummary(limit: number = 10, mode: string = 'local', sensitivity?: number) {
  const res = await api.get('/ml/anomaly-summary', { 
    params: { limit, mode, ...(sensitivity != null && { sensitivity }) },
    withCredentials: true 
  });
  return res.data;
}

// Get prediction summary
export async function getPredictionSummary(limit: number = 10, mode: string = 'local', hoursAhead?: number) {
  const res = await api.get('/ml/prediction-summary', { 
    params: { limit, mode, ...(hoursAhead != null && { hoursAhead }) },
    withCredentials: true 
  });
  return res.data;
}

// Reports API

// Get report definitions
export async function getReportDefinitions() {
  const res = await api.get('/reports/definitions', { withCredentials: true });
  return res.data;
}

// Generate a report
export async function generateReport(payload: {
  reportType: string;
  title?: string;
  period: { from: Date | string; to: Date | string };
  filters?: any;
  description?: string;
  save?: boolean;
}) {
  const res = await api.post('/reports/generate', {
    ...payload,
    period: {
      from: payload.period.from instanceof Date ? payload.period.from.toISOString() : payload.period.from,
      to: payload.period.to instanceof Date ? payload.period.to.toISOString() : payload.period.to,
    }
  }, { withCredentials: true });
  return res.data;
}

// Export a report (CSV, HTML, JSON)
export async function exportReport(payload: {
  runId?: string;
  generatePayload?: {
    reportType: string;
    title?: string;
    period: { from: Date | string; to: Date | string };
    filters?: any;
    description?: string;
  };
  format: 'CSV' | 'HTML' | 'JSON';
  persist?: boolean;
}) {
  const exportPayload: any = {
    format: payload.format,
    persist: payload.persist || false,
  };

  if (payload.runId) {
    exportPayload.runId = payload.runId;
  } else if (payload.generatePayload) {
    exportPayload.generatePayload = {
      ...payload.generatePayload,
      period: {
        from: payload.generatePayload.period.from instanceof Date 
          ? payload.generatePayload.period.from.toISOString() 
          : payload.generatePayload.period.from,
        to: payload.generatePayload.period.to instanceof Date 
          ? payload.generatePayload.period.to.toISOString() 
          : payload.generatePayload.period.to,
      }
    };
  }

  const res = await api.post('/reports/export', exportPayload, {
    withCredentials: true,
    responseType: 'json', // Backend returns JSON with body, filename, contentType
  });
  
  // Backend returns: { contentType, filename, body, updatedId }
  const { filename, body, contentType } = res.data;
  
  // Create a blob from the body content
  // For CSV, add BOM (Byte Order Mark) for Excel compatibility
  let blobContent = body;
  if (payload.format === 'CSV' && typeof body === 'string') {
    // Add UTF-8 BOM for Excel compatibility
    blobContent = '\uFEFF' + body;
  }
  
  const blob = new Blob([blobContent], { 
    type: contentType || (payload.format === 'CSV' ? 'text/csv;charset=utf-8;' : 'text/plain')
  });

  return { blob, filename: filename || `report.${payload.format.toLowerCase()}` };
}

// Get report runs
export async function getReportRuns(params: {
  limit?: number;
  skip?: number;
  type?: string;
  days?: number;
} = {}) {
  const res = await api.get('/reports/runs', {
    params,
    withCredentials: true
  });
  return res.data;
}

// Get report run by ID
export async function getReportRun(id: string) {
  const res = await api.get(`/reports/run/${id}`, { withCredentials: true });
  return res.data;
}

// Groups Management APIs

// Get all groups
export async function getGroups(params?: { organizationId?: string; withMembers?: boolean }) {
  const res = await api.get('/groups', { params, withCredentials: true });
  return res.data;
}

// Get group by ID
export async function getGroup(id: string, params?: { withMembers?: boolean }) {
  const res = await api.get(`/groups/${id}`, { params, withCredentials: true });
  return res.data;
}

// Create a new group
export async function createGroup(groupData: {
  name: string;
  memberIds?: string[];
  organizationId?: string;
}) {
  const res = await api.post('/groups', groupData, { withCredentials: true });
  return res.data;
}

// Update group
export async function updateGroup(id: string, groupData: {
  name?: string;
}) {
  const res = await api.put(`/groups/${id}`, groupData, { withCredentials: true });
  return res.data;
}

// Delete group
export async function deleteGroup(id: string) {
  const res = await api.delete(`/groups/${id}`, { withCredentials: true });
  return res.data;
}

// Set group members (replace all members)
export async function setGroupMembers(id: string, memberIds: string[]) {
  const res = await api.put(`/groups/${id}/members`, { memberIds }, { withCredentials: true });
  return res.data;
}

// Add members to group
export async function addGroupMembers(id: string, memberIds: string[]) {
  const res = await api.post(`/groups/${id}/members/add`, { memberIds }, { withCredentials: true });
  return res.data;
}

// Remove members from group
export async function removeGroupMembers(id: string, memberIds: string[]) {
  const res = await api.post(`/groups/${id}/members/remove`, { memberIds }, { withCredentials: true });
  return res.data;
}